import React, { useEffect, useRef, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import Navbar from "./components/navbar/navbar";
import Footer from "./components/footer/footer";
import AdminAnnouncement from "./pages/admin/AdminAnnouncement";
import { AnnouncementProvider } from "./context/AnnouncementContext";
import { SiteContentProvider } from "./context/SiteContentContext";
import Home from "./pages/home/home";
import { HOME_SECTIONS } from "./components/site/siteText";
import AdminAnnouncementList from "./pages/admin/AdminAnnouncementList";
import AdminAnnouncementEdit from "./pages/admin/AdminAnnouncementEdit";
import AdminLogin from "./pages/admin/AdminLogin";
import { checkAdminSession } from "./pages/admin/adminAuth";
import UploadAnnouncement from "./pages/admin/UploadAnnouncement";
import DailyPdfGenerator from "./pages/admin/DailyPdfGenerator";
import AdminDailyAnnouncementManager from "./pages/admin/AdminDailyAnnouncementManager";
import AdminContentEditor from "./pages/admin/AdminContentEditor";
import AdminRouteLayout from "./components/admin/AdminRouteLayout";
import DirectorChangePage from "./pages/form/DirectorChangePage";
import FinancialApprovalPage from "./pages/form/FinancialApprovalPage";
import DividedPaymantPage from "./pages/form/DividedPayment";
import CompanyRegulationsPage from "./pages/form/CompanyRegulations";
import CapitalReductionPage from "./pages/form/CapitalReduction";
import AmendmentOfObjectivesPage from "./pages/form/AmendmentofObjectives";
import MeetingInvitationENPage from "./pages/form/MeetingInvitationEN";
import Notice1Page from "./pages/form/Notice1";
import Notice2Page from "./pages/form/Notice2";
import Notice3Page from "./pages/form/Notice3";
import Notice4Page from "./pages/form/Notice4";
import NoticeofCapitalReductionPage from "./pages/form/NoticeofCapitalReduction";
import InvitationEGM_ENPage from "./pages/form/InvitationEGM_EN";
import InvitationEGM2_ENPage from "./pages/form/InvitationEGM2_EN";
import InvitationEGM3_ENPage from "./pages/form/InvitationEGM3_EN";
import NoticeTransformPartnershipToCompany_THPage from "./pages/form/NoticeTransformPartnershipToCompany_TH";
import DistributePage from "./pages/form/Distribute";
import RaiseFundsPage from "./pages/form/RaiseFunds";
import RelocationPage from "./pages/form/Relocation";
import DissolutionPage from "./pages/form/Dissolution";
import ExcelToPdfPage from "./pages/form/ExcelToPdfPage";
import AdminEditor from "./pages/admin/AdminEditor";
import AdminArticles from "./pages/admin/AdminArticles";
import ArticlePage from "./pages/newspaper/ArticlePage";
import NewspaperHome from "./pages/newspaper/NewspaperHome";
import Edition from "./pages/newspaper/Edition";
import SubmitPage from "./pages/newspaper/SubmitPage";
import StatusPage from "./pages/newspaper/StatusPage";
import CheckoutPage from "./pages/newspaper/CheckoutPage";
import NotFound from "./pages/NotFound";

// Server decides: /api/admin/me checks the HttpOnly session cookie
function RequireAdminAuth({ children }) {
  const location = useLocation();
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    let active = true;
    checkAdminSession().then((ok) => {
      if (active) setStatus(ok ? "ok" : "denied");
    });
    return () => { active = false; };
  }, [location.pathname]);

  if (status === "checking") return null;
  if (status === "denied") {
    return <Navigate to="/admin" replace state={{ from: location }} />;
  }
  return children;
}

// Older Excel imports store these template routes (backend formSchemas); send them to the real form page and keep
// ?previewId= / ?importId= so the imported values still load.
const LEGACY_FORM_ROUTES = {
  "/Dividend": "/DividedPaymant",
  "/ChangeObjective": "/AmendmentofObjectives",
  "/ReduceCapital": "/CapitalReduction",
  "/AmendCompany": "/CompanyRegulations",
  "/Dissolution1": "/Notice1Form",
  "/Dissolution2": "/Notice2Form",
  "/Dissolution3": "/Notice3Form",
  "/Dissolution4": "/Notice4Form",
  "/ConvertPartnership": "/NoticeTransformPartnershipToCompany_TH",
  "/CapitalIncrease": "/RaiseFunds",
};

function RedirectKeepQuery({ to }) {
  const location = useLocation();
  return <Navigate to={{ pathname: to, search: location.search }} replace />;
}

function App() {
  const navbarRef = useRef(null);

  useEffect(() => {
    // วัดความสูง navbar จริงแล้วอัปเดต CSS variable --navbar-height
    // ทำให้ทุก component เลื่อนลงมาพอดีกับความสูง navbar ที่แท้จริง
    const updateNavbarHeight = () => {
      if (!navbarRef.current) return;
      const h = navbarRef.current.offsetHeight;
      document.documentElement.style.setProperty("--navbar-height", `${h}px`);
    };

    updateNavbarHeight();

    // ติดตามการเปลี่ยนแปลงขนาด navbar (responsive resize)
    const ro = new ResizeObserver(updateNavbarHeight);
    if (navbarRef.current) ro.observe(navbarRef.current);

    // แก้ bfcache: เมื่อ browser restore หน้าจาก cache
    // ให้ measure ใหม่เพื่อ layout ถูกต้อง
    const handlePageShow = (e) => {
      if (e.persisted) {
        // หน้าถูก restore จาก bfcache → re-measure
        updateNavbarHeight();
      }
    };
    window.addEventListener("pageshow", handlePageShow);

    return () => {
      ro.disconnect();
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, []);

  return (
    <SiteContentProvider>
        <AnnouncementProvider>
          <div className="app-wrapper">

            {/* Sticky header wrapper — ResizeObserver keeps --navbar-height in sync */}
            <div ref={navbarRef} className="app-header-navbar">
              <Navbar />
            </div>

            <div className="app-content">
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/newspaper" element={<NewspaperHome />} />
                <Route path="/newspaper/editions/:date" element={<Edition />} />
                <Route path="/newspaper/submit" element={<SubmitPage />} />
                <Route path="/newspaper/status" element={<StatusPage />} />
                <Route path="/newspaper/checkout" element={<CheckoutPage />} />
                <Route path="/newspaper/articles/:id" element={<ArticlePage />} />
                {/* "เข้าสู่ระบบ" = admin sign-in; the editor desk lives in the admin console */}
                <Route path="/newspaper/track" element={<Navigate to="/admin" replace />} />
                <Route path="/editor" element={<Navigate to="/admin/editor" replace />} />
                {/* old public routes → redesigned pages */}
                <Route path="/announce" element={<Navigate to="/newspaper/submit" replace />} />
                <Route path="/download" element={<Navigate to="/newspaper" replace />} />
                <Route path="/packages" element={<Navigate to="/services" replace />} />
                <Route path="/about" element={<Navigate to="/group" replace />} />
                {/* home page sections with their own address: #/services, #/group, #/contact … (not #/#services) */}
                {HOME_SECTIONS.map((s) => <Route key={s} path={`/${s}`} element={<Home />} />)}
                <Route path="/admin" element={<AdminLogin />} />
                <Route
                  path="/admin"
                  element={
                    <RequireAdminAuth>
                      <AdminRouteLayout />
                    </RequireAdminAuth>
                  }
                >
                  <Route path="announce" element={<AdminAnnouncement />} />
                  <Route path="upload-announcement" element={<UploadAnnouncement />} />
                  <Route path="daily-pdf" element={<DailyPdfGenerator />} />
                  <Route path="daily-announcements" element={<AdminDailyAnnouncementManager />} />
                  <Route path="announcements" element={<AdminAnnouncementList />} />
                  <Route path="announcements/:id/edit" element={<AdminAnnouncementEdit />} />
                  <Route path="announcements/new" element={<AdminAnnouncementEdit />} />
                  <Route path="content" element={<AdminContentEditor />} />
                  <Route path="editor" element={<AdminEditor />} />
                  <Route path="articles" element={<AdminArticles />} />
                  <Route path="/admin/excel-to-pdf" element={<ExcelToPdfPage />} />
                </Route>
                <Route path="/FinancialApproval" element={<FinancialApprovalPage />} />
                <Route path="/DirectorChange" element={<DirectorChangePage />} />
                <Route path="/DividedPaymant" element={<DividedPaymantPage />} />
                <Route path="/CompanyRegulations" element={<CompanyRegulationsPage />} />
                <Route path="/CapitalReduction" element={<CapitalReductionPage />} />
                <Route path="/AmendmentofObjectives" element={<AmendmentOfObjectivesPage />} />
                <Route path="/MeetingInvitationEN" element={<MeetingInvitationENPage />} />
                <Route path="/Notice1Form" element={<Notice1Page />} />
                <Route path="/Notice2Form" element={<Notice2Page />} />
                <Route path="/Notice3Form" element={<Notice3Page />} />
                <Route path="/Notice4Form" element={<Notice4Page />} />
                <Route path="/NoticeofCapitalReduction" element={<NoticeofCapitalReductionPage />} />
                <Route path="/InvitationEGM_EN" element={<InvitationEGM_ENPage />} />
                <Route path="/InvitationEGM2_EN" element={<InvitationEGM2_ENPage />} />
                <Route path="/InvitationEGM3_EN" element={<InvitationEGM3_ENPage />} />
                <Route path="/NoticeTransformPartnershipToCompany_TH" element={<NoticeTransformPartnershipToCompany_THPage />} />
                <Route path="/Distribute" element={<DistributePage />} />
                <Route path="/RaiseFunds" element={<RaiseFundsPage />} />
                <Route path="/Relocation" element={<RelocationPage />} />
                <Route path="/Dissolution" element={<DissolutionPage />} />
                {Object.entries(LEGACY_FORM_ROUTES).map(([from, to]) => (
                  <Route key={from} path={from} element={<RedirectKeepQuery to={to} />} />
                ))}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </div>

            <Footer />
          </div>
        </AnnouncementProvider>
    </SiteContentProvider>
  );
}

export default App;
